import React from 'react'
import { styled } from '@mui/material'
import Box from '@mui/material/Box'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import BarChart from '../Charts/BarChart/BarChart'
import * as CHART_UTILS from '../Charts/BarChart/BarChart.constant'

const CardHeader = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '12px',
  borderBottom: '1px solid lightgray'
}))

const SelectBox = styled('select')(() => ({
  borderRadius: '4px',
  padding: '4px 8px'
}))

const OptionBox = styled('option')(() => ({
  padding: '4px 8px'
}))

const shiftMap = {
  s1: {
    value: 's1',
    label: 'Shift-1',
    chartLegend: 'S1',
    energyKey: 'shift1Energy'
  },
  s2: {
    value: 's2',
    label: 'Shift-2',
    chartLegend: 'S2',
    energyKey: 'shift2Energy'
  },
  day: {
    value: 'day',
    label: 'Day',
    chartLegend: 'Day',
    energyKey: 'dayEnergy'
  }
}

const EnergyTrend = ({ energyTrend }) => {
  const [isCombined, setIsCombined] = React.useState(false)
  const [selectedShift, setSelectedShift] = React.useState(shiftMap.day.value)

  const chartTitle = 'Energy trend'
  const chartLabels = energyTrend ? energyTrend.map((el) => el['date']) : []

  const prepareChartData = (data) => {
    return {
      s1energy: data.map((el) => el['shift1Energy']),
      s2energy: data.map((el) => el['shift2Energy']),
      dayTotalEnergy: data.map((el) => el['dayEnergy'])
    }
  }

  const { s1energy, s2energy, dayTotalEnergy } = energyTrend
    ? prepareChartData(energyTrend)
    : { s1energy: [], s2energy: [], dayTotalEnergy: [] }

  const calculateTrendLine = (data, isCombined, selectedShift) => {
    if (isCombined) {
      return data.map((el) => {
        const shift1 = el.shift1Energy || 0
        const shift2 = el.shift2Energy || 0
        const day = el.dayEnergy || 0
        return Math.max(shift1, shift2, day)
      })
    } else {
      return data.map((el) => {
        const value = el[shiftMap[selectedShift].energyKey]
        return value || 0
      })
    }
  }

  const trendLineData = energyTrend
    ? calculateTrendLine(energyTrend, isCombined, selectedShift)
    : []

  const totalEnergyConsumption = energyTrend
    ? energyTrend.reduce((acc, cv) => {
        if (isCombined) {
          const shift1 = cv.shift1Energy || 0
          const shift2 = cv.shift2Energy || 0
          const day = cv.dayEnergy || 0
          return acc + Math.max(shift1, shift2, day)
        } else {
          const energyKey = shiftMap[selectedShift].energyKey
          return acc + (cv[energyKey] || 0)
        }
      }, 0)
    : 0

  const combinedDataGroup = [
    {
      ...CHART_UTILS.BAR,
      label: 'S1 Energy',
      data: s1energy,
      backgroundColor: '#7afade',
      borderColor: '#04b890'
    },
    {
      ...CHART_UTILS.BAR,
      label: 'S2 Energy',
      data: s2energy,
      backgroundColor: '#f5ae67',
      borderColor: '#b55d05'
    },
    {
      ...CHART_UTILS.BAR,
      label: 'Day Energy',
      data: dayTotalEnergy,
      backgroundColor: '#fa9ba6',
      borderColor: '#fa465b'
    },
    {
      type: 'line',
      label: 'Trend Line',
      data: trendLineData,
      borderColor: '#0000ff',
      borderWidth: 2,
      fill: false,
      pointRadius: 0
    }
  ]

  const dataGroup = energyTrend
    ? [
        {
          ...CHART_UTILS.BAR,
          label: `${shiftMap[selectedShift].chartLegend} Energy`,
          data: energyTrend.map((el) => el[shiftMap[selectedShift].energyKey]),
          backgroundColor: '#7afade',
          borderColor: '#04b890'
        },
        {
          type: 'line',
          label: 'Trend Line',
          data: trendLineData,
          borderColor: '#0000ff',
          borderWidth: 2,
          fill: false,
          pointRadius: 0
        }
      ]
    : []

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        border: '1px solid gray',
        borderRadius: '12px'
      }}
    >
      <CardHeader>
        <h3>{chartTitle}</h3>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'flex-end'
          }}
        >
          <SelectBox
            disabled={isCombined}
            value={selectedShift}
            onChange={(event) => setSelectedShift(event.target.value)}
          >
            {Object.values(shiftMap).map((item) => (
              <OptionBox key={item.value} value={item.value}>
                {item.label}
              </OptionBox>
            ))}
          </SelectBox>
        </Box>
      </CardHeader>
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '0 12px'
          }}
        >
          <Typography sx={{ fontWeight: 400, padding: '12px' }}>
            Total energy consumption:{' '}
            <span style={{ fontWeight: 600 }}>
              {totalEnergyConsumption.toFixed(2)} KWh
            </span>
          </Typography>
          <label style={{ marginLeft: 'auto' }}>Combined</label>

          <Switch
            checked={isCombined}
            onChange={(event) => setIsCombined(event.target.checked)}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </Box>

        {energyTrend && (
          <BarChart
            chartTitle={chartTitle}
            chartLabels={chartLabels}
            dataGroup={isCombined ? combinedDataGroup : dataGroup}
            xLabel="DAYS"
            yLabel="Energy consumption (KWh)"
          />
        )}
      </Box>
    </Box>
  )
}

export default EnergyTrend
